import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { uploadEvidenceFile } from '../services/uploadService';
import { mapUploadError } from '../utils/uploadErrors';

const DEFAULT_CONCURRENCY = 3;

const createTask = ({ questionId, file }) => ({
  id: `${questionId}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  questionId,
  responseId: null,
  file,
  name: file.name,
  size: file.size,
  progress: 0,
  status: 'queued', // queued | uploading | success | failed | canceled | paused_auth | paused_network
  error: '',
  createdAt: Date.now(),
});

export const useUploadManager = ({
  responseType = 'cobit',
  concurrency = DEFAULT_CONCURRENCY,
  ensureResponse, // async (questionId) => responseId
  onUploadSuccess, // (questionId, uploadedFile) => void
  onUploadError, // optional (task, err) => void
}) => {
  const [tasks, setTasks] = useState([]);
  const [isPaused, setIsPaused] = useState(false);

  const controllersRef = useRef(new Map()); // taskId -> AbortController
  const tickingRef = useRef(false);
  const diskFullToastShownRef = useRef(false);

  const activeCount = useMemo(
    () => tasks.filter((t) => t.status === 'uploading').length,
    [tasks]
  );

  const pendingCount = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status === 'queued' ||
          t.status === 'uploading' ||
          t.status === 'paused_auth' ||
          t.status === 'paused_network'
      ).length,
    [tasks]
  );

  const getTasksByQuestion = useCallback(
    (questionId) => tasks.filter((t) => t.questionId === questionId && t.status !== 'success'),
    [tasks]
  );

  const markTask = useCallback((taskId, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
  }, []);

  const removeTask = useCallback((taskId) => {
    const ctrl = controllersRef.current.get(taskId);
    if (ctrl) {
      ctrl.abort();
      controllersRef.current.delete(taskId);
    }
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const cancelUpload = useCallback(
    (taskId) => {
      const ctrl = controllersRef.current.get(taskId);
      if (ctrl) {
        ctrl.abort();
        controllersRef.current.delete(taskId);
      }
      markTask(taskId, { status: 'canceled', error: 'Canceled by user' });
    },
    [markTask]
  );

  const retryUpload = useCallback(
    (taskId) => {
      markTask(taskId, { status: 'queued', progress: 0, error: '' });
    },
    [markTask]
  );

  const pauseAll = useCallback(() => setIsPaused(true), []);
  const resumeAll = useCallback(() => setIsPaused(false), []);

  const requeuePaused = useCallback(() => {
    setTasks((prev) =>
      prev.map((t) =>
        t.status === 'paused_auth' || t.status === 'paused_network'
          ? { ...t, status: 'queued', error: '' }
          : t
      )
    );
  }, []);

    const addFiles = useCallback(({ questionId, files }) => {
    const list = Array.from(files || []);
    if (!list.length) {
        return {
        added: 0,
        skippedDuplicate: 0,
        duplicateFiles: [],
        acceptedFiles: [],
        rejectedFiles: [],
        };
    }

    let skippedDuplicate = 0;
    const duplicateFiles = [];
    const acceptedFiles = [];
    const rejectedFiles = [];

    setTasks((prev) => {
        const next = [...prev];

        for (const file of list) {
        const duplicate = next.some(
            (t) =>
            t.questionId === questionId &&
            t.name === file.name &&
            t.size === file.size &&
            ['queued', 'uploading', 'paused_auth', 'paused_network'].includes(t.status)
        );

        if (duplicate) {
            skippedDuplicate += 1;

            const rejected = {
            name: file.name,
            size: file.size,
            reason: `${file.name} is already in the upload queue.`,
            code: 'DUPLICATE_FILE',
            };

            duplicateFiles.push({ name: file.name, size: file.size });
            rejectedFiles.push(rejected);
            continue;
        }

        next.push(createTask({ questionId, file }));
        acceptedFiles.push({ name: file.name, size: file.size });
        }

        return next;
    });

    return {
        added: acceptedFiles.length,
        skippedDuplicate,
        duplicateFiles,
        acceptedFiles,
        rejectedFiles,
    };
    }, []);

  const runTask = useCallback(
    async (task) => {
      const ctrl = new AbortController();
      controllersRef.current.set(task.id, ctrl);

      try {
        markTask(task.id, { status: 'uploading', error: '', progress: 1 });

        const responseId = await ensureResponse(task.questionId);
        if (!responseId) throw new Error('Could not create/load response before upload.');

        markTask(task.id, { responseId });

        const res = await uploadEvidenceFile({
          file: task.file,
          responseId,
          responseType,
          signal: ctrl.signal,
          onUploadProgress: (evt) => {
            const total = evt.total || task.size || 1;
            const percent = Math.min(100, Math.round((evt.loaded / total) * 100));
            markTask(task.id, { progress: percent });
          },
        });

        const uploadedFile = {
          id: res.data.id,
          name: res.data.file_name || task.name,
          file_url: res.data.file_url,
          uploaded_at: new Date().toISOString(),
        };

        onUploadSuccess(task.questionId, uploadedFile);
        markTask(task.id, { status: 'success', progress: 100, error: '' });
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          markTask(task.id, { status: 'canceled', error: 'Canceled by user' });
        } else {
          const mapped = mapUploadError(err);

          if (mapped.code === 'UNAUTHORIZED') {
            markTask(task.id, {
              status: 'paused_auth',
              error: 'Session expired. Sign in again, then retry/resume.',
            });
          } else if (mapped.code === 'NETWORK_ERROR') {
            markTask(task.id, {
              status: 'paused_network',
              error: 'Network issue. Upload will resume when connection is restored.',
            });
          } else {
            // CORS_BLOCKED, PAYLOAD_TOO_LARGE, UNSUPPORTED_MEDIA_TYPE, etc.
            markTask(task.id, {
              status: 'failed',
              error: mapped.message,
            });
          }

          if (mapped.code === 'SERVER_DISK_FULL' && !diskFullToastShownRef.current) {
            diskFullToastShownRef.current = true;
            toast.error('Server storage is full. Some uploads failed. Please contact support.');
          }

          onUploadError?.(task, err);
        }
      } finally {
        controllersRef.current.delete(task.id);
      }
    },
    [ensureResponse, markTask, onUploadError, onUploadSuccess, responseType]
  );

  const tick = useCallback(async () => {
    if (tickingRef.current) return;
    tickingRef.current = true;

    try {
      if (isPaused) return;

      const snapshot = [...tasks];
      const uploading = snapshot.filter((t) => t.status === 'uploading').length;
      const slots = Math.max(0, concurrency - uploading);
      if (slots <= 0) return;

      const queued = snapshot
        .filter((t) => t.status === 'queued')
        .sort((a, b) => a.createdAt - b.createdAt)
        .slice(0, slots);

      await Promise.all(queued.map((t) => runTask(t)));
    } finally {
      tickingRef.current = false;
    }
  }, [tasks, concurrency, isPaused, runTask]);

  useEffect(() => {
    tick();
  }, [tick]);

  useEffect(() => {
    const hasDiskFullFailed = tasks.some(
      (t) => t.status === 'failed' && t.error?.toLowerCase().includes('storage is full')
    );
    if (!hasDiskFullFailed) {
      diskFullToastShownRef.current = false;
    }
  }, [tasks]);

  useEffect(() => {
    const onOnline = () => {
      requeuePaused();
    };

    const onAuthRecovered = () => {
      requeuePaused();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('auth:recovered', onAuthRecovered);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('auth:recovered', onAuthRecovered);
    };
  }, [requeuePaused]);

  useEffect(() => {
    const handler = (e) => {
      if (pendingCount > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingCount]);

  const clearFinished = useCallback(() => {
    setTasks((prev) =>
      prev.filter(
        (t) =>
          t.status !== 'success' &&
          t.status !== 'canceled'
      )
    );
  }, []);

  return {
    tasks,
    addFiles,
    retryUpload,
    removeTask,
    cancelUpload,
    pauseAll,
    resumeAll,
    requeuePaused,
    clearFinished,
    getTasksByQuestion,
    isPaused,
    activeCount,
    pendingCount,
  };
};